import React, { useState, useEffect, useId } from "react";
import { DocumentText } from "iconsax-reactjs";
import "./MediaCaptionModal.css";

// Preview + optional caption before sending media, matching the mobile media
// preview screen. Multiple images become one captioned album; other files send
// individually with the caption on the first.
export default function MediaCaptionModal({ files, onSend, onCancel, uploading, progress }) {
  const [caption, setCaption] = useState("");
  const [previews, setPreviews] = useState([]);
  const titleId = useId();
  const captionId = useId();

  useEffect(() => {
    const media = (files || [])
      .filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"))
      .map((f) => ({ url: URL.createObjectURL(f), type: f.type.startsWith("video/") ? "video" : "image", name: f.name }));
    setPreviews(media);
    return () => media.forEach((m) => URL.revokeObjectURL(m.url));
  }, [files]);

  if (!files || files.length === 0) return null;

  const docFiles = files.filter((f) => !f.type.startsWith("image/") && !f.type.startsWith("video/"));

  return (
    <div className="modal-overlay" onClick={uploading ? undefined : onCancel}>
      <div
        className="modal media-caption-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id={titleId}>{files.length === 1 ? "Send attachment" : `Send ${files.length} attachments`}</h3>
          <button type="button" className="btn-icon" onClick={onCancel} disabled={uploading} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="media-caption-previews">
            {previews.map((p, i) => (
              p.type === "video"
                ? <video key={i} src={p.url} className="media-caption-thumb" muted />
                : <img key={i} src={p.url} alt={p.name} className="media-caption-thumb" />
            ))}
            {docFiles.map((f, i) => (
              <div key={`d${i}`} className="media-caption-doc">
                <span className="media-caption-doc-icon"><DocumentText size={18} variant="Linear" /></span>
                <span className="media-caption-doc-name">{f.name}</span>
              </div>
            ))}
          </div>

          <div className="field">
            <label className="field-label" htmlFor={captionId}>
              Caption <span className="muted">(optional)</span>
            </label>
            <input
              id={captionId}
              type="text"
              className="input"
              placeholder="Add a caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSend(caption.trim()); }}
              disabled={uploading}
              autoFocus
            />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={uploading}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => onSend(caption.trim())} disabled={uploading}>
            {uploading ? <><span className="btn-spinner" /> {progress}%</> : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
